// Removing Markdown that a vision model added to OCR output.
//
// WHY THIS EXISTS: TYPHOON_OCR_PROMPT used to end "Return clean Markdown", and
// the result is treated as verbatim source text. So the model's formatting
// landed in the `requirement` field and from there in the Excel export — a
// contractual document with `####` in it.
//
// The damage was not cosmetic. `matchClauseRef` requires a line to START with
// its number, so `#### ๓.๑.๓ …` produced no ref at all. Measured on a real
// 24-page AMR TOR (2026-08-21): 503 rows, of which 24 had a real clause
// reference destroyed by a heading marker, including the document's top-level
// sections `## ๓.` and `### ๓.๑`.
//
// IS REMOVING IT A VERBATIM VIOLATION? No — the reverse. The markers are not in
// the source; the model added them because we asked. Removing them restores the
// text. That was verified rather than assumed: the source page was rendered and
// read, and the line begins `๓.๑.` with no `#` anywhere in the document.
//
// The prompt no longer asks for Markdown. This exists because a prompt is a
// request, not a guarantee — vision models emit Markdown out of habit — and
// because it repairs text extracted before the prompt was fixed.

/**
 * The punctuation Markdown escapes with a backslash.
 *
 * A backslash before one of these is Markdown syntax, not content: Thai prose
 * does not use backslashes at all. Seen in the wild as `\-` at the start of a
 * line — the model transcribed the source's own dash bullet, then escaped it so
 * Markdown would not re-interpret it as a list marker.
 */
const MD_ESCAPE = /\\([\\`*_{}[\]()#+\-.!|>~])/g;

/**
 * Markdown a Thai TOR cannot plausibly contain: ATX headings, bold spans, and
 * backslash escapes. Any of the three proves the model was formatting.
 */
const UNAMBIGUOUS = /^#{1,6}\s+|\*\*[^*\n]+\*\*|\\[\\`*_{}[\]()#+\-.!|>~]/;

/**
 * Whether this text shows a model was formatting as Markdown.
 *
 * Used to decide the one ambiguous case (below). Judged over the WHOLE document
 * rather than per line: one `####` proves the model was in Markdown mode, which
 * then explains every `*` in the same response.
 */
export const looksLikeMarkdown = (text: string): boolean =>
  text.split("\n").some((l) => UNAMBIGUOUS.test(l.trim()));

/**
 * Strip Markdown a model added, leaving everything else byte-for-byte.
 *
 * Three rules, in descending order of certainty:
 *
 *  1. `^#{1,6} ` — removed. A Thai TOR does not use ATX headings, and removing
 *     the marker is what lets the clause reference underneath be found again.
 *  2. `**bold**` — unwrapped, text kept. Paired double asterisks are Markdown;
 *     Thai typography has no equivalent.
 *  3. `^* ` — removed entirely, and gated on `allowBulletRewrite`.
 *
 *     This was first written to rewrite `* ` to `- `, on the reasoning that a
 *     dash is how these documents write bullets natively. **That was wrong, and
 *     checking the source is what showed it.** The source page was rendered and
 *     read: a numbered list appears as
 *
 *         ๑. ปริมาณงาน
 *             ๑.๑ ก่อสร้างบ่อสูบน้ำ …
 *
 *     with **no marker of any kind** — just indentation. Typhoon invented the
 *     bullet. Rewriting it to a dash therefore replaced one invented character
 *     with another, and left `- ๑.๑ …`, where the marker still hid the clause
 *     reference from `matchClauseRef` exactly as `####` had.
 *
 *     Removing it restores the source and recovers the reference in one step.
 *
 *     The residual uncertainty is stated rather than hidden: it is *proven* that
 *     the marker is invented before a clause number, and only *inferred* for
 *     plain bulleted text — the inference being that the same response used `-`
 *     for 145 lines and `*` for 62, so the two are not transcribing the same
 *     glyph. The evidence gate keeps this confined to responses that demonstrably
 *     contain Markdown.
 *
 * A leading `- ` is NEVER touched: real TORs use dash bullets, and 145 rows of
 * the measured document did.
 */
export function stripMarkdown(line: string, allowBulletRewrite: boolean): string {
  let out = line;

  // 1. Heading markers — only at the start, only when followed by space.
  out = out.replace(/^(\s*)#{1,6}[ \t]+/, "$1");

  // 2. Bold spans. Runs until no pair is left, so `**a** and **b**` both go.
  //    Requires non-empty, single-line content so a stray `**` is left alone.
  let prev: string;
  do {
    prev = out;
    out = out.replace(/\*\*([^*\n]+)\*\*/g, "$1");
  } while (out !== prev);

  // 3. Bullet marker — REMOVED, not rewritten. Gated on document-level evidence.
  if (allowBulletRewrite) out = out.replace(/^(\s*)\*[ \t]+/, "$1");

  // 4. Backslash escapes — ungated, like headings and bold, because Thai prose
  //    contains no backslashes at all. `\- text` becomes `- text`: the dash is
  //    the SOURCE's (the model was escaping it precisely to preserve it), so
  //    only the backslash goes. Runs last so it cannot re-expose a marker that
  //    the earlier rules were meant to remove.
  out = out.replace(MD_ESCAPE, "$1");

  return out;
}

/**
 * Clean one OCR response.
 *
 * `allowBulletRewrite`, if given, OVERRIDES the text's own evidence — pass the
 * result of {@link poolMarkdownEvidence} across every page of the document
 * this text came from. Omit it only when `text` genuinely is the whole
 * document (tests, or a single-call engine); every multi-page caller MUST
 * pool first.
 *
 * WHY THIS MATTERS: found live, 2026-08-21, on a re-extraction of the same
 * 24-page TOR that motivated this file. `ocrPDFTyphoon` calls Typhoon once per
 * PAGE and used to run this function on each page's text in isolation — so
 * "the document proves Markdown mode" was actually only ever "this one page
 * does". A page carrying nothing but a numbered list wrapped in `*` bullets,
 * with no `####` heading of its own, never tripped the gate, and its
 * asterisks — and the clause references behind them — were left untouched.
 * The row directly above it, from the same OCR run, had already lost its `*`
 * to a heading on ITS page. Evidence has to be pooled across the whole
 * extraction for the design in {@link stripMarkdown} to mean what it claims.
 *
 * Line count is preserved exactly — the row splitter downstream is line-based,
 * and dropping or adding a line here would silently change the matrix.
 */
export function cleanModelMarkdown(text: string, allowBulletRewrite?: boolean): string {
  const allow = allowBulletRewrite ?? looksLikeMarkdown(text);
  return text
    .split("\n")
    .map((l) => stripMarkdown(l, allow))
    .join("\n");
}

/**
 * Whether ANY page of a multi-page OCR run shows Markdown — the flag every
 * caller with more than one page must compute and pass to every
 * {@link cleanModelMarkdown} call for that document.
 */
export const poolMarkdownEvidence = (pageTexts: readonly string[]): boolean =>
  pageTexts.some((t) => looksLikeMarkdown(t));
